import React, { useEffect, useMemo, useState } from 'react';

import cx from 'classnames';

import Text from 'lib/common/components/Text';
import { TTextTypeKeys } from 'lib/common/components/Text/constants/textTypes';

import IQCursorOrb from '../../../common/IQCursorOrb/IQCursorOrb';
import { chunkString } from './utils/chunkString';

import styles from './streaming-text.module.scss';

interface StreamingTextProps {
  disableAnimation?: boolean;
  text: string[] | string;
  fontType?: TTextTypeKeys;
  speed?: number; // milliseconds per character
  onComplete?: () => void;
  containerClassName?: string;
  textClassName?: string;
}

export const StreamingText: React.FC<StreamingTextProps> = ({
  disableAnimation = false,
  text,
  fontType = 'body',
  speed = 10,
  onComplete,
  containerClassName,
  textClassName,
}) => {
  const [displayedText, setDisplayedText] = useState<string[]>([]);
  const [currentParagraphIndex, setCurrentParagraphIndex] = useState(0);
  const [currentCharIndex, setCurrentCharIndex] = useState(0);

  const chunkText = () => {
    const textArray = Array.isArray(text) ? text : [text];
    const totalCharCount = textArray.join('').length;
    // if size is greater than 200, chunk into 3 characters per frame, otherwise chunk into 1 character per frame
    const chunkSize = totalCharCount > 200 ? 3 : 1;

    const paragraphChunks = textArray.map((paragraph) => chunkString(paragraph, chunkSize));

    return {
      textArray,
      paragraphChunks,
      chunkSize,
    };
  };

  const { textArray, paragraphChunks, chunkSize } = useMemo(() => chunkText(), [text]);

  useEffect(() => {
    // animation disabled
    if (disableAnimation) {
      setDisplayedText(textArray);
      onComplete?.();
      return;
    }

    // no text to display
    if (textArray.length === 0) {
      return;
    }

    // all paragraphs displayed
    if (currentParagraphIndex >= textArray.length) {
      onComplete?.();
      return;
    }

    const currentParagraphChunks = paragraphChunks[currentParagraphIndex];
    const hasMoreChunksInParagraph = currentCharIndex < currentParagraphChunks.length;

    // move to next paragraph
    if (!hasMoreChunksInParagraph) {
      setCurrentParagraphIndex((prev) => prev + 1);
      setCurrentCharIndex(0);
      return;
    }

    // display next chunk
    const currentCharacterChunk = currentParagraphChunks[currentCharIndex];
    const timeout = setTimeout(() => {
      setDisplayedText((prev) => {
        const newText = [...prev];
        if (!newText[currentParagraphIndex]) {
          newText[currentParagraphIndex] = '';
        }
        newText[currentParagraphIndex] += currentCharacterChunk;
        return newText;
      });
      setCurrentCharIndex((prev) => prev + 1);
    }, speed);

    return () => clearTimeout(timeout);
  }, [currentCharIndex, currentParagraphIndex, paragraphChunks, textArray, chunkSize]);

  const shouldShowCursor = currentParagraphIndex < textArray.length && !disableAnimation;

  return (
    <div className={cx(styles['streaming-text'], containerClassName)}>
      {displayedText.map((paragraph, index) => (
        <Text type={fontType} key={index} className={textClassName}>
          {paragraph}
          {shouldShowCursor && currentParagraphIndex === index && <IQCursorOrb speed="FAST" />}
        </Text>
      ))}
    </div>
  );
};
